import type { AccountInfo } from "@azure/msal-browser";
import { msalInstance, loginRequest } from "./msal";

const API_BASE = import.meta.env["VITE_API_BASE_URL"] ?? "/api";
const REQUEST_TIMEOUT_MS = 25_000;

export class ApiError extends Error {
  status: number;
  detail?: string;
  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

async function getAccessToken(): Promise<string> {
  const account: AccountInfo | null = msalInstance.getActiveAccount();
  if (account === null) {
    throw new ApiError(401, "Not signed in");
  }

  try {
    let result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });

    // MSAL may return a CACHED id token that is already expired (id tokens live
    // ~1h; acquireTokenSilent only guarantees a fresh *access* token). Check the
    // id token's exp and force a refresh if it's expired or within 60s of it.
    const claims = result.idTokenClaims as { exp?: number } | undefined;
    const nowSec = Math.floor(Date.now() / 1000);
    if (claims?.exp === undefined || claims.exp <= nowSec + 60) {
      result = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
    }

    return result.idToken;
  } catch {
    // Silent acquisition failed (e.g. refresh token expired) — needs interactive sign-in.
    await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    throw new ApiError(401, "Session expired — redirecting to sign in");
  }
}

interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  body?: unknown;
  signal?: AbortSignal;
  /** Override the default 25s timeout — AI calls (coaching, scoring) run longer. */
  timeoutMs?: number;
}

/**
 * Fetch wrapper that adds Authorization: Bearer <id-token>, times out (25s by
 * default, override via timeoutMs), and throws ApiError on non-2xx. JSON in,
 * JSON out.
 */
export async function apiCall<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const token = await getAccessToken();

  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  const signal = options.signal ?? controller.signal;

  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal,
    });

    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      let detail: string | undefined;
      try {
        const body = (await response.json()) as { error?: string; detail?: string };
        if (body.error !== undefined) message = body.error;
        if (body.detail !== undefined) detail = body.detail;
      } catch {
        /* body wasn't JSON */
      }
      throw new ApiError(response.status, message, detail);
    }

    return (await response.json()) as T;
  } finally {
    window.clearTimeout(timer);
  }
}

/**
 * Like apiCall, but returns the response as a Blob and triggers a browser
 * download via a synthetic <a download> click. Used by CSV / PDF endpoints
 * where the server emits raw bytes instead of JSON.
 */
export async function apiDownload(path: string, filename: string, options: ApiOptions = {}): Promise<void> {
  const token = await getAccessToken();
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), options.timeoutMs ?? REQUEST_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}${path}`, {
      method: options.method ?? "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        ...(options.body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
      signal: options.signal ?? controller.signal,
    });
    if (!response.ok) {
      let message = `HTTP ${response.status}`;
      try {
        const body = (await response.json()) as { error?: string };
        if (body.error !== undefined) message = body.error;
      } catch { /* not JSON */ }
      throw new ApiError(response.status, message);
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } finally {
    window.clearTimeout(timer);
  }
}
